import React, { useEffect, useRef, useState } from 'react'

import cn from 'classnames'

import Field from '../../../Field/Field'
import FieldLabel from '../../../FieldLabel/FieldLabel'
import Help from '../../../Help/Help'
import TextInput from '../../../TextInput/TextInput'
import Textarea from '../../../Textarea/Textarea'
import Dropdown, { type IList } from '../../../DropDown/Dropdown'
import Checkbox from '../../../Checkbox/Checkbox'
import { InstructionText } from '../../../InstructionText/InstructionText'
import {
  URL_FORMATTING_CASE_OPTIONS,
  URL_FORMATTING_WORD_SEPARATOR_OPTIONS,
  URL_FORMATTING_MAP_JSON_MAX_LENGTH,
  DEFAULT_URL_FORMATTING_REGEX_PATTERN,
  DEFAULT_URL_FORMATTING_REGEX_REPLACE_WITH,
  commitUrlFieldFormattingOptions,
  formattingMapToJsonString,
  getUrlFieldFormattingOptions,
  parseFormattingMapJson,
  type SelectOption,
  type UrlFieldFormattingOptions,
  type UrlFormattingRegexOptions
} from './utils/urlFormattingOptionsUtils'

type UrlFormattingOptionsPatch = Partial<Omit<UrlFieldFormattingOptions, 'regex'>> & {
  regex?: Partial<UrlFormattingRegexOptions>
}

import './UrlFormattingOptions.css'

export const URL_FORMATTING_PRESET_DISABLED_MESSAGE =
  'URL formatting is disabled because URL Preset is turned off. Enable the Preset Enabled toggle on the Basic tab to configure these options.'

export type UrlFormattingOptionsProps = {
  uniqueKey: string
  schema: { formatting_options?: UrlFieldFormattingOptions }
  onNotifyChange: () => void
  /** When URL Preset is off (`url_pattern === false`), formatting controls are read-only. */
  disabled?: boolean
  t?: (key: string) => string
}

/** Stable DOM ids (no dots) — `uniqueKey` often contains `.` and breaks `Textarea` querySelector. */
const URL_FORMATTING_FIELD_IDS = {
  mapJson: 'cs-ct-field-url-formatting-map-json',
  fallback: 'cs-ct-field-url-formatting-fallback-value',
  regexPattern: 'cs-ct-field-url-formatting-regex-pattern',
  regexReplaceWith: 'cs-ct-field-url-formatting-regex-replace-with'
} as const

const buildFormattingDropdownList = <T extends string>(
  options: ReadonlyArray<SelectOption<T>>,
  currentValue: T,
  onSelect: (value: T) => void
): IList[] =>
  options.map((option) => ({
    label: option.example ? (
      <span className="UrlFormattingOptions__dropdownOption">
        <span className="UrlFormattingOptions__dropdownOptionLabel">{option.label}</span>
        <span className="UrlFormattingOptions__dropdownOptionExample">{option.example}</span>
      </span>
    ) : (
      option.label
    ),
    /** Header display — Dropdown renders `selectedData` from viewAs, not textLabel. */
    value: option.label,
    textLabel: option.label,
    action: () => onSelect(option.value),
    default: currentValue === option.value
  }))

const UrlFormattingOptions = ({
  uniqueKey,
  schema,
  onNotifyChange,
  disabled = false,
  t = (key: string) => ''
}: UrlFormattingOptionsProps) => {
  const formatting = getUrlFieldFormattingOptions(schema)
  const mapJsonFromSchema = formattingMapToJsonString(formatting.map)

  const [mapJsonText, setMapJsonText] = useState(mapJsonFromSchema)
  const lastCommittedMapJsonRef = useRef(mapJsonFromSchema)

  const [regexPattern, setRegexPattern] = useState(() => formatting.regex.pattern)
  const [regexReplaceWith, setRegexReplaceWith] = useState(() => formatting.regex.replaceWith)

  useEffect(() => {
    setRegexPattern(formatting.regex.pattern)
    setRegexReplaceWith(formatting.regex.replaceWith)
  }, [schema.formatting_options?.regex?.pattern, schema.formatting_options?.regex?.replaceWith])

  useEffect(() => {
    if (mapJsonFromSchema === lastCommittedMapJsonRef.current) {
      return
    }
    lastCommittedMapJsonRef.current = mapJsonFromSchema
    setMapJsonText(mapJsonFromSchema)
  }, [mapJsonFromSchema])

  const persist = (patch: UrlFormattingOptionsPatch) => {
    if (disabled) {
      return
    }
    const current = getUrlFieldFormattingOptions(schema)
    commitUrlFieldFormattingOptions(schema, {
      ...current,
      ...patch,
      regex: patch.regex ? { ...current.regex, ...patch.regex } : current.regex,
      map: patch.map !== undefined ? patch.map : current.map
    })
    onNotifyChange()
  }

  const caseDropdownList = buildFormattingDropdownList(
    URL_FORMATTING_CASE_OPTIONS,
    formatting.case,
    (value) => persist({ case: value })
  )

  const wordSeparatorDropdownList = buildFormattingDropdownList(
    URL_FORMATTING_WORD_SEPARATOR_OPTIONS,
    formatting.word_separator,
    (value) => persist({ word_separator: value })
  )

  const applyValidMapJson = (json: string, normalizeText = false): boolean => {
    const { map, valid } = parseFormattingMapJson(json)
    if (!valid) {
      return false
    }
    const committedJson = formattingMapToJsonString(map)
    lastCommittedMapJsonRef.current = committedJson
    persist({ map })
    if (normalizeText && committedJson !== json) {
      setMapJsonText(committedJson)
    }
    return true
  }

  const onDefaultEmptyValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    persist({ substitute_missing_value: event.target.value })
  }

  const onMapJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value
    if (next.length > URL_FORMATTING_MAP_JSON_MAX_LENGTH) {
      return
    }
    setMapJsonText(next)
    applyValidMapJson(next)
  }

  const onMapJsonBlur = () => {
    if (mapJsonText.trim() === '') {
      lastCommittedMapJsonRef.current = ''
      persist({ map: {} })
      setMapJsonText('')
      return
    }
    applyValidMapJson(mapJsonText, true)
  }

  const onRegexPatternChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    setRegexPattern(next)
    persist({ regex: { pattern: next } })
  }

  const onRegexReplaceWithChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    setRegexReplaceWith(next)
    persist({ regex: { replaceWith: next } })
  }

  const onRegexGlobalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    persist({ regex: { global: event.target.checked } })
  }

  const onRegexCaseSensitiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    persist({ regex: { caseInsensitive: !event.target.checked } })
  }

  const { regex } = formatting

  return (
    <div
      className={cn('UrlFormattingOptions', { 'UrlFormattingOptions--disabled': disabled })}
      data-test-id="cs-ct-field-url-formatting-options"
    >
          <FieldLabel className='mb-8' version="v2" testId="cs-form-field-url-options" htmlFor="options">{t('ContentTypeFieldProperties.UrlFormattingOptions.url_formatting') || 'URL Formatting'}  <Help
                      testId="cs-form-field-url-formatting-help-text"
                      type="basic"
                      text={
                        disabled
                          ? t('ContentTypeFieldProperties.UrlFormattingOptions.url_formatting_disabled_message') || URL_FORMATTING_PRESET_DISABLED_MESSAGE
                          : t('ContentTypeFieldProperties.UrlFormattingOptions.url_formatting_help') || 'Automatically format generated URLs using casing, separators, mappings, and regex rules.'
                      }
                    />
                    </FieldLabel>

      {disabled ? (
        <InstructionText
          className="UrlFormattingOptions__presetDisabledInstruction"
          testId="cs-ct-field-url-formatting-preset-disabled-instruction"
        >
          {t('ContentTypeFieldProperties.UrlFormattingOptions.url_formatting_disabled_message') || URL_FORMATTING_PRESET_DISABLED_MESSAGE}
        </InstructionText>
      ) : null}

      <div className="UrlFormattingOptions__indent">
        <Field className="UrlFormattingOptions__field" disabled={disabled}>
          <FieldLabel
            className="FieldLabel__CT UrlFormattingOptions__fieldLabel"
            version="v2"
            htmlFor="cs-ct-field-url-formatting-case-select"
            testId="cs-ct-field-url-formatting-case-label"
          >
            {t('ContentTypeFieldProperties.UrlFormattingOptions.casing') || 'Casing'}
          </FieldLabel>
          <Dropdown
            className="FormFields__Dropdown_v2 UrlFormattingOptions__dropdown"
            type="select"
            viewAs="value"
            arrowSecondary={true}
            dropDownType="primary"
            list={caseDropdownList}
            closeAfterSelect={true}
            version="v2"
            isDisabled={disabled}
            testId="cs-ct-field-url-formatting-case-select"
          />
          <InstructionText
            className="UrlFormattingOptions__instruction"
            testId="cs-ct-field-url-formatting-case-instruction"
          >
            {t('ContentTypeFieldProperties.UrlFormattingOptions.casing_instruction') || 'Controls how letters appear in the URL.'}
          </InstructionText>
        </Field>

        <Field className="UrlFormattingOptions__field" disabled={disabled}>
          <FieldLabel
            className="FieldLabel__CT UrlFormattingOptions__fieldLabel"
            version="v2"
            htmlFor="cs-ct-field-url-formatting-word-separator-select"
            testId="cs-ct-field-url-formatting-word-separator-label"
          >
            {t('ContentTypeFieldProperties.UrlFormattingOptions.word_separator') || 'Word Separator'}
          </FieldLabel>
          <Dropdown
            className="FormFields__Dropdown_v2 UrlFormattingOptions__dropdown"
            type="select"
            viewAs="value"
            arrowSecondary={true}
            dropDownType="primary"
            list={wordSeparatorDropdownList}
            closeAfterSelect={true}
            version="v2"
            isDisabled={disabled}
            testId="cs-ct-field-url-formatting-word-separator-select"
          />
          <InstructionText
            className="UrlFormattingOptions__instruction"
            testId="cs-ct-field-url-formatting-word-separator-instruction"
          >
            {t('ContentTypeFieldProperties.UrlFormattingOptions.word_separator_instruction') || 'Choose how words are separated in the URL.'}
          </InstructionText>
        </Field>

        <Field className="UrlFormattingOptions__field" disabled={disabled}>
          <FieldLabel
            className="FieldLabel__CT UrlFormattingOptions__fieldLabel"
            version="v2"
            testId="cs-ct-field-url-formatting-map-label"
            htmlFor={URL_FORMATTING_FIELD_IDS.mapJson}
          >
            {t('ContentTypeFieldProperties.UrlFormattingOptions.json_mapping') || 'JSON Mapping'}
          </FieldLabel>
          <Help
            type="basic"
            testId="cs-ct-field-url-formatting-map-help"
            text={t('ContentTypeFieldProperties.UrlFormattingOptions.json_mapping_help') || 'Define custom character or string replacements for URL formatting.'}
          />
          <Textarea
            id={URL_FORMATTING_FIELD_IDS.mapJson}
            name={`${uniqueKey}.formattingMapJson`}
            version="v2"
            width="full"
            placeholder={t('ContentTypeFieldProperties.UrlFormattingOptions.json_mapping_placeholder') || 'Define key-value pairs for mapping transformations'}
            value={mapJsonText}
            onChange={onMapJsonChange}
            onBlur={onMapJsonBlur}
            showCharacterCount
            maxLength={URL_FORMATTING_MAP_JSON_MAX_LENGTH}
            disabled={disabled}
            testId="cs-ct-field-url-formatting-map-json"
          />
        </Field>

        <Field className="UrlFormattingOptions__subsection" disabled={disabled}>
          <div className="UrlFormattingOptions__subsectionHeader">
            <span className="UrlFormattingOptions__subsectionTitle">{t('ContentTypeFieldProperties.UrlFormattingOptions.default') || 'Default'}</span>
            <Help
              type="basic"
              testId="cs-ct-field-url-formatting-default-help"
              text={t('ContentTypeFieldProperties.UrlFormattingOptions.default_help') || 'Used when fields referenced in the URL pattern are empty or undefined.'}
            />
          </div>
          <div className="UrlFormattingOptions__subsectionBody">
            <Field className="UrlFormattingOptions__field UrlFormattingOptions__field--nested" disabled={disabled}>
              <FieldLabel
                className="FieldLabel__CT UrlFormattingOptions__fieldLabel"
                version="v2"
                testId="cs-ct-field-url-formatting-fallback-label"
                htmlFor={URL_FORMATTING_FIELD_IDS.fallback}
              >
                {t('ContentTypeFieldProperties.UrlFormattingOptions.fallback_value') || 'Fallback Value'}
              </FieldLabel>
              <TextInput
                id={URL_FORMATTING_FIELD_IDS.fallback}
                name={`${uniqueKey}.formattingFallback`}
                version="v2"
                width="full"
                placeholder={t('ContentTypeFieldProperties.UrlFormattingOptions.fallback_placeholder') || 'Enter fallback value'}
                value={formatting.substitute_missing_value}
                onChange={onDefaultEmptyValueChange}
                disabled={disabled}
                testId="cs-ct-field-url-formatting-fallback-value"
              />
              <InstructionText
                className="UrlFormattingOptions__instruction"
                testId="cs-ct-field-url-formatting-fallback-instruction"
              >
                {t('ContentTypeFieldProperties.UrlFormattingOptions.fallback_instruction') || 'Value to use when the field is empty or undefined. Leave empty for no default.'}
              </InstructionText>
            </Field>
          </div>
        </Field>

        <Field className="UrlFormattingOptions__subsection" disabled={disabled}>
          <div className="UrlFormattingOptions__subsectionHeader">
            <span className="UrlFormattingOptions__subsectionTitle">{t('ContentTypeFieldProperties.UrlFormattingOptions.regex') || 'Regex'}</span>
            <Help
              type="basic"
              testId="cs-ct-field-url-formatting-regex-help"
              text={t('ContentTypeFieldProperties.UrlFormattingOptions.regex_help') || 'Apply regex-based find-and-replace rules to format generated URLs.'}
            />
          </div>
          <div className="UrlFormattingOptions__subsectionBody">
            <Field className="UrlFormattingOptions__field UrlFormattingOptions__field--nested" disabled={disabled}>
              <FieldLabel
                className="FieldLabel__CT UrlFormattingOptions__fieldLabel"
                version="v2"
                testId="cs-ct-field-url-formatting-regex-pattern-label"
                htmlFor={URL_FORMATTING_FIELD_IDS.regexPattern}
              >
                {t('ContentTypeFieldProperties.UrlFormattingOptions.regex_pattern') || 'Pattern'}
              </FieldLabel>
              <TextInput
                id={URL_FORMATTING_FIELD_IDS.regexPattern}
                name={`${uniqueKey}.regexPattern`}
                version="v2"
                width="full"
                placeholder={t('ContentTypeFieldProperties.UrlFormattingOptions.regex_pattern_placeholder') || 'Enter a pattern'}
                value={regexPattern}
                onChange={onRegexPatternChange}
                disabled={disabled}
                testId="cs-ct-field-url-formatting-regex-pattern"
              />
              <InstructionText
                className="UrlFormattingOptions__instruction"
                testId="cs-ct-field-url-formatting-regex-pattern-instruction"
              >
                {t('ContentTypeFieldProperties.UrlFormattingOptions.regex_pattern_instruction') || 'Enter the regular expression pattern to match.'}
              </InstructionText>
            </Field>
            <Field className="UrlFormattingOptions__field UrlFormattingOptions__field--nested" disabled={disabled}>
              <FieldLabel
                className="FieldLabel__CT UrlFormattingOptions__fieldLabel"
                version="v2"
                testId="cs-ct-field-url-formatting-regex-replace-label"
                htmlFor={URL_FORMATTING_FIELD_IDS.regexReplaceWith}
              >
                {t('ContentTypeFieldProperties.UrlFormattingOptions.regex_replace_with') || 'Replace With'}
              </FieldLabel>
              <TextInput
                id={URL_FORMATTING_FIELD_IDS.regexReplaceWith}
                name={`${uniqueKey}.regexReplaceWith`}
                version="v2"
                width="full"
                placeholder={t('ContentTypeFieldProperties.UrlFormattingOptions.regex_replace_placeholder') || 'Enter a replacement value'}
                value={regexReplaceWith}
                onChange={onRegexReplaceWithChange}
                disabled={disabled}
                testId="cs-ct-field-url-formatting-regex-replace-with"
              />
              <InstructionText
                className="UrlFormattingOptions__instruction"
                testId="cs-ct-field-url-formatting-regex-replace-instruction"
              >
                {t('ContentTypeFieldProperties.UrlFormattingOptions.regex_replace_instruction') || 'Enter the replacement value for matched patterns.'}
              </InstructionText>
            </Field>
            <div className="UrlFormattingOptions__checkboxRow">
              <Checkbox
                testId="cs-ct-field-url-formatting-regex-global"
                name={`${uniqueKey}.regexGlobal`}
                label={t('ContentTypeFieldProperties.UrlFormattingOptions.regex_global') || 'Global (replace all occurrences)'}
                checked={regex.global}
                onChange={onRegexGlobalChange}
                disabled={disabled}
              />
              <Checkbox
                testId="cs-ct-field-url-formatting-regex-case-sensitive"
                name={`${uniqueKey}.regexCaseSensitive`}
                label={t('ContentTypeFieldProperties.UrlFormattingOptions.regex_case_sensitive') || 'Case Sensitive'}
                checked={!regex.caseInsensitive}
                onChange={onRegexCaseSensitiveChange}
                disabled={disabled}
              />
            </div>
          </div>
        </Field>
      </div>
    </div>
  )
}

export default UrlFormattingOptions
